import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { t } from "@/i18n-js/instance";
import type { CheckoutProps } from "@/react/components/Paywalls/Checkout/types/CheckoutProps";
import { ErrorTemplate } from "@circle-react/components/shared/ErrorPages/ErrorTemplate";
import { reportCheckoutError } from "./checkoutErrorHelpers";

interface CheckoutErrorBoundaryProps {
  checkoutProps: CheckoutProps;
  children?: ReactNode;
}

interface CheckoutErrorBoundaryState {
  hasError: boolean;
}

export class CheckoutErrorBoundary extends Component<
  CheckoutErrorBoundaryProps,
  CheckoutErrorBoundaryState
> {
  constructor(props: CheckoutErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (window.DISABLE_JS_ERROR_BOUNDARIES === "true") {
      throw error;
    }
    this.setState({ hasError: true });

    const { checkoutProps } = this.props;
    const { currentCommunity, currentCommunityMember, currentUser, paywall } =
      checkoutProps;
    reportCheckoutError(error, {
      communityId: currentCommunity?.id,
      communityMemberId: currentCommunityMember?.id,
      communityMemberUserId: currentUser?.id,
      extra: { error_info_component_stack: errorInfo.componentStack },
      paywallId: paywall?.id,
    });
  }

  override render(): ReactNode {
    const { children } = this.props;
    const { hasError } = this.state;

    if (hasError) {
      return (
        <ErrorTemplate
          errorCode={500}
          title={t("paywall_checkout.error_boundary.title")}
        />
      );
    }

    return children;
  }
}
